import type { NavigationEntry } from "@positive/ui";
import "@ui5/webcomponents-icons/dist/activity-items.js";
import "@ui5/webcomponents-icons/dist/batch-payments.js";
import "@ui5/webcomponents-icons/dist/building.js";
import "@ui5/webcomponents-icons/dist/collections-insight.js";
import "@ui5/webcomponents-icons/dist/compare.js";
import "@ui5/webcomponents-icons/dist/credit-card.js";
import "@ui5/webcomponents-icons/dist/customer-financial-fact-sheet.js";
import "@ui5/webcomponents-icons/dist/document.js";
import "@ui5/webcomponents-icons/dist/expense-report.js";
import "@ui5/webcomponents-icons/dist/home-share.js";
import "@ui5/webcomponents-icons/dist/inbox.js";
import "@ui5/webcomponents-icons/dist/journey-change.js";
import "@ui5/webcomponents-icons/dist/legend.js";
import "@ui5/webcomponents-icons/dist/letter.js";
import "@ui5/webcomponents-icons/dist/line-chart.js";
import "@ui5/webcomponents-icons/dist/money-bills.js";
import "@ui5/webcomponents-icons/dist/monitor-payments.js";
import "@ui5/webcomponents-icons/dist/official-service.js";
import "@ui5/webcomponents-icons/dist/payment-approval.js";
import "@ui5/webcomponents-icons/dist/receipt.js";
import "@ui5/webcomponents-icons/dist/request.js";
import "@ui5/webcomponents-icons/dist/sales-document.js";
import "@ui5/webcomponents-icons/dist/settings.js";
import "@ui5/webcomponents-icons/dist/suitcase.js";
import "@ui5/webcomponents-icons/dist/wallet.js";

import { cashPermissions } from "./permissions";

/** Cash & Banks entries of the navigation registry (group `cashBanks`, plan module `finance`). */
export const cashBanksNavigation: readonly NavigationEntry[] = [
  {
    key: "cash.receiptVouchers",
    labelKey: "nav.receiptVouchers",
    icon: "receipt",
    path: "/cash/receipt-vouchers",
    permission: cashPermissions.receiptView,
  },
  {
    key: "cash.paymentVouchers",
    labelKey: "nav.paymentVouchers",
    icon: "payment-approval",
    path: "/cash/payment-vouchers",
    permission: cashPermissions.paymentView,
  },
  {
    key: "cash.cheques",
    labelKey: "nav.cheques",
    icon: "collections-insight",
    path: "/cash/cheques",
    permission: cashPermissions.chequeView,
  },
  {
    key: "cash.promissoryNotes",
    subGroup: "guarantees",
    labelKey: "nav.promissoryNotes",
    icon: "document",
    path: "/cash/promissory-notes",
    permission: cashPermissions.promissoryView,
  },
  {
    key: "cash.guaranteeCheques",
    subGroup: "guarantees",
    labelKey: "nav.guaranteeCheques",
    icon: "legend",
    path: "/cash/guarantee-cheques",
    permission: cashPermissions.guaranteeChequeView,
  },
  {
    key: "cash.bankDeposits",
    labelKey: "nav.bankDeposits",
    icon: "batch-payments",
    path: "/cash/bank-deposits",
    permission: cashPermissions.depositView,
  },
  {
    key: "cash.cashTransfers",
    labelKey: "nav.cashTransfers",
    icon: "journey-change",
    path: "/cash/cash-transfers",
    permission: cashPermissions.transferView,
  },
  {
    key: "cash.cashCounts",
    subGroup: "pettyCash",
    labelKey: "nav.cashCounts",
    icon: "money-bills",
    path: "/cash/cash-counts",
    permission: cashPermissions.cashCountView,
  },
  {
    key: "cash.bankReconciliations",
    labelKey: "nav.bankReconciliations",
    icon: "compare",
    path: "/cash/bank-reconciliations",
    permission: cashPermissions.reconciliationView,
  },
  {
    key: "cash.cardSettlements",
    subGroup: "pettyCash",
    labelKey: "nav.cardSettlements",
    icon: "credit-card",
    path: "/cash/card-settlements",
    permission: cashPermissions.cardSettlementView,
  },
  {
    key: "cash.debitNotes",
    subGroup: "notes",
    labelKey: "nav.debitNotes",
    icon: "sales-document",
    path: "/cash/debit-notes",
    permission: cashPermissions.debitNoteView,
  },
  {
    key: "cash.creditNotes",
    subGroup: "notes",
    labelKey: "nav.creditNotes",
    icon: "customer-financial-fact-sheet",
    path: "/cash/credit-notes",
    permission: cashPermissions.creditNoteView,
  },
  {
    key: "cash.paymentRequests",
    subGroup: "notes",
    labelKey: "nav.paymentRequests",
    icon: "request",
    path: "/cash/payment-requests",
    permission: cashPermissions.paymentRequestView,
  },
  {
    key: "cash.debitNoteRequests",
    subGroup: "notes",
    labelKey: "nav.debitNoteRequests",
    icon: "inbox",
    path: "/cash/debit-note-requests",
    permission: cashPermissions.debitNoteRequestView,
  },
  {
    key: "cash.creditNoteRequests",
    subGroup: "notes",
    labelKey: "nav.creditNoteRequests",
    icon: "inbox",
    path: "/cash/credit-note-requests",
    permission: cashPermissions.creditNoteRequestView,
  },
  {
    key: "cash.prepaidExpenses",
    subGroup: "pettyCash",
    labelKey: "nav.prepaidExpenses",
    icon: "expense-report",
    path: "/cash/prepaid-expenses",
    permission: cashPermissions.prepaidView,
  },
  {
    key: "cash.lettersOfGuarantee",
    subGroup: "guarantees",
    labelKey: "nav.lettersOfGuarantee",
    icon: "official-service",
    path: "/cash/letters-of-guarantee",
    permission: cashPermissions.guaranteeView,
  },
  {
    key: "cash.lettersOfCredit",
    subGroup: "guarantees",
    labelKey: "nav.lettersOfCredit",
    icon: "letter",
    path: "/cash/letters-of-credit",
    permission: cashPermissions.creditLetterView,
  },
  {
    key: "cash.pettyCashExpenses",
    subGroup: "pettyCash",
    labelKey: "nav.pettyCashExpenses",
    icon: "wallet",
    path: "/cash/petty-cash-expenses",
    permission: cashPermissions.pettyCashView,
  },
  {
    key: "cash.pettyCashReplenishments",
    subGroup: "pettyCash",
    labelKey: "nav.pettyCashReplenishments",
    icon: "money-bills",
    path: "/cash/petty-cash-replenishments",
    permission: cashPermissions.replenishmentView,
  },
  {
    key: "cash.cashFlowForecast",
    labelKey: "nav.cashFlowForecast",
    icon: "line-chart",
    path: "/cash/cash-flow-forecast",
    permission: cashPermissions.forecastView,
  },
  {
    key: "cash.forecastSettings",
    subGroup: "setup",
    labelKey: "nav.forecastSettings",
    icon: "settings",
    path: "/cash/cash-flow-forecast/settings",
    permission: cashPermissions.forecastView,
  },
  {
    key: "cash.banks",
    subGroup: "setup",
    labelKey: "nav.cashBanksBanks",
    icon: "building",
    path: "/cash/setup/banks",
    permission: cashPermissions.bankView,
  },
  {
    key: "cash.cashAccounts",
    subGroup: "setup",
    labelKey: "nav.cashAccounts",
    icon: "home-share",
    path: "/cash/setup/cash-accounts",
    permission: cashPermissions.cashAccountView,
  },
  {
    key: "cash.chequeBooks",
    subGroup: "setup",
    labelKey: "nav.chequeBooks",
    icon: "activity-items",
    path: "/cash/setup/cheque-books",
    permission: cashPermissions.chequeBookView,
  },
  {
    key: "cash.cardAcquirers",
    subGroup: "setup",
    labelKey: "nav.cardAcquirers",
    icon: "monitor-payments",
    path: "/cash/setup/card-acquirers",
    permission: cashPermissions.cardAcquirerView,
  },
  {
    key: "cash.pettyCashFunds",
    subGroup: "setup",
    labelKey: "nav.pettyCashFunds",
    icon: "suitcase",
    path: "/cash/setup/petty-cash-funds",
    permission: cashPermissions.pettyCashFundView,
  },
  {
    key: "cash.postingSettings",
    subGroup: "setup",
    labelKey: "nav.cashPostingSettings",
    icon: "settings",
    path: "/cash/setup/posting-settings",
    permission: cashPermissions.settingsView,
  },
  {
    key: "cash.settings",
    subGroup: "setup",
    labelKey: "nav.cashSettings",
    icon: "settings",
    path: "/platform/settings/cash",
    permission: cashPermissions.settingsView,
  },
];
